import type { PropsWithChildren } from "react";
import { memo, useCallback, useMemo } from "react";

import Event from "@mui/icons-material/Event";
import type {
  DatePickerProps,
  DateValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers-pro";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import isNil from "lodash/isNil";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { MOBILE_MEDIA_QUERY } from "@waterly/utils-isomorphic";

import { useDateFormats } from "../../../providers/DateFormatsProvider";
import { DatePickerActionBar } from "../DatePickerActionBar";

const localTimeZone = DateTime.now().zoneName;

export type BasicDatePickerProps = Omit<
  DatePickerProps<DateTime>,
  | "value"
  | "onChange"
  | "onAccept"
  | "disableHighlightToday"
  | "desktopModeMediaQuery"
  | "maxDate"
  | "minDate"
  | "format"
  | "timezone"
> & {
  //value should be in the timezone that you want to be displayed. (i.e. the value should already be in the proper timeZone)
  value: DateTime | null;
  minDate?: DateTime;
  maxDate?: DateTime;
  onChange?: (value: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => void;
  onAccept?: (value: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => void;
  format?: string;
  //timeZone onChange and onAccept the time will be returned in
  timeZone?: string;
  required?: boolean;
};

// Backwards compatibility alias for existing consumers
export type BasicDateWidgetProps = BasicDatePickerProps;

const BasicDatePickerComponent = ({
  value,
  onChange,
  onAccept,
  slotProps,
  slots,
  format,
  minDate,
  maxDate,
  timeZone = localTimeZone,
  required = false,
  ...props
}: PropsWithChildren<BasicDatePickerProps>) => {
  const { t } = useTranslation(["common"]);
  const { date: dateFormat } = useDateFormats();

  const applyTimeZone = useCallback(
    (dateTime: DateTime | null, timeZone: string): DateTime | null =>
      dateTime ? DateTime.fromObject(dateTime.toObject(), { zone: timeZone }) : null,
    [],
  );

  /**
   * This is the Localized DateTime for display purposes only
   */
  const displayDateTime = useMemo(() => {
    if (!value) return null;
    return applyTimeZone(value, localTimeZone);
  }, [applyTimeZone, value]);

  const handleOnChange: Required<DatePickerProps<DateTime>>["onChange"] = useCallback(
    (date, context) => {
      if (!onChange) return;
      if (!date) return onChange(null, context);
      const newDt = applyTimeZone(date, timeZone);
      onChange(newDt, context);
    },
    [applyTimeZone, onChange, timeZone],
  );

  const handleOnAccept: Required<DatePickerProps<DateTime>>["onAccept"] = useCallback(
    (date, context) => {
      if (!onAccept) return;
      if (!date) return onAccept(null, context);
      const newDt = applyTimeZone(date, timeZone);
      onAccept(newDt, context);
    },
    [applyTimeZone, onAccept, timeZone],
  );

  /**
   * This is a live check that reflects what the timezone being displayed is, DO NOT REMOVE!!!
   * aka it reflects the value prop, not the timeZone prop
   */
  const zoneLabel = useMemo(() => {
    const zone = value?.zoneName;
    return isNil(zone) ? "" : t("common:datepicker_timezone_label", { zoneName: zone });
  }, [value?.zoneName, t]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-us">
      <DatePicker
        value={displayDateTime}
        onChange={handleOnChange}
        onAccept={handleOnAccept}
        disableOpenPicker={false}
        closeOnSelect
        maxDate={maxDate}
        minDate={minDate}
        slotProps={{
          toolbar: { hidden: true },
          actionBar: { actions: ["cancel", "today"] },
          textField: {
            fullWidth: true,
            required,
            InputProps: { endAdornment: <Event /> },
            helperText: zoneLabel,
          },
          ...slotProps,
        }}
        slots={{
          actionBar: DatePickerActionBar,
          ...slots,
        }}
        {...props}
        desktopModeMediaQuery={MOBILE_MEDIA_QUERY}
        format={format ?? dateFormat}
        // Highlighting today doesn't honor timezone. To avoid confusion, we always disable it for now.
        disableHighlightToday
      />
    </LocalizationProvider>
  );
};

export const BasicDatePicker = memo(BasicDatePickerComponent);

export default BasicDatePicker;
